import { useRef } from "react";
import { Button, List, Space, Typography, message } from "antd";
import { UploadOutlined, DeleteOutlined, FileOutlined } from "@ant-design/icons";
import { useUploadFile } from "./useUploadFile";
import { FileItem } from "@services/api";

const { Text } = Typography;

interface Props {
  value?: FileItem[];
  onChange?: (files: FileItem[]) => void;
  multiple?: boolean;
}

export default function FilesAttach({ value = [], onChange, multiple = true }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { upload, loading } = useUploadFile();

  const openPicker = () => inputRef.current?.click();

  const handleRemove = (fileId: string) => {
    const next = value.filter((f) => f.fileId !== fileId);
    onChange?.(next);
  };

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // cбрасываем value у input, чтобы можно было выбрать тот же файл повторно
    e.target.value = "";

    if (!files.length) return;

    try {
      const results = await Promise.allSettled(files.map((f) => upload(f)));
      const uploaded: FileItem[] = [];
      results.forEach((r, idx) => {
        if (r.status === "fulfilled") {
          uploaded.push(r.value);
        } else {
          message.error(`Не удалось загрузить файл: ${files[idx].name}`);
          // eslint-disable-next-line no-console
          console.error("Upload error", r.reason);
        }
      });

      if (uploaded.length) {
        if (multiple) {
          onChange?.([...value, ...uploaded]);
        } else {
          // берём последний успешно загруженный файл и затираем предыдущий
          onChange?.([uploaded[0]]);
        }
      }
    } catch (err) {
      message.error("Ошибка загрузки файлов");
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        onChange={handleSelect}
        style={{ display: "none" }}
      />

      <Button
        type="default"
        icon={<UploadOutlined />}
        onClick={openPicker}
        loading={loading}
      >
        Выбрать файлы
      </Button>

      {value.length > 0 && (
        <List
          style={{ marginTop: 12 }}
          size="small"
          dataSource={value}
          renderItem={(f) => (
            <List.Item
              actions={[
                <Button
                  key="delete"
                  type="text"
                  shape="circle"
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemove(f.fileId)}
                />,
              ]}
            >
              <Space size={8}>
                <FileOutlined />
                <Text>{f.fileName}</Text>
                <Text type="secondary">
                  {typeof f.size === "number" ? `${Math.round(f.size / 1024)} КБ` : ""}
                </Text>
              </Space>
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
